/**
 * The model's provider credentials, entered here and stored encrypted.
 *
 * This replaces a free-text "Connection ID" box whose own hint read "No partner
 * connections exist yet — bind later". There was no way to supply a key while
 * creating a model: the operator had to leave for Partners → Connections, build
 * a connection there, and paste its raw ObjectId back in. And the connection
 * form could only offer one bearer token plus a JSON blob, so the providers
 * needing more than a token — Azure (endpoint + api-version), Bedrock (region),
 * Vertex (service account + project + location) — could not be configured at
 * all. A model saved fine and returned 503 on its first prompt run.
 *
 * Fields are rendered from GET /ai-models/provider-specs rather than hardcoded,
 * so a provider that gains a field gains it here without a frontend release.
 *
 * There is deliberately no choice of WHERE the credential comes from. The
 * backend still serves a model with no connection from the server's own .env,
 * and that is how the seeded global models work — but nobody using this screen
 * can edit .env, so offering it as an option meant offering something the
 * operator could not act on and could not fix.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Password } from 'primereact/password';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';

import { connectionTypesService } from '../../services/connection-types.service';
import { partnerConnectionsService } from '../../services/partner-connections.service';
import type { AiProviderField, AiProviderSpec } from '../../types';
import { EMPTY_CREDENTIALS, defaultsFor, type CredentialValue } from './model-credentials';

const STALE = 5 * 60 * 1000;

interface Props {
  providerType?: string;
  spec?: AiProviderSpec;
  value: CredentialValue;
  onChange: (next: CredentialValue) => void;
  errors: Record<string, string>;
  /** The model's project — scopes the "use existing connection" list. */
  appId?: string;
}

const FieldInput = ({ field, value, onChange, invalid }: {
  field: AiProviderField;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) => {
  const common = {
    value,
    className: `w-full${invalid ? ' p-invalid' : ''}`,
    placeholder: field.placeholder || '',
  };
  if (field.kind === 'json') {
    return (
      <InputTextarea
        {...common}
        rows={5}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}
      />
    );
  }
  if (field.kind === 'password') {
    return (
      <Password
        value={value}
        onChange={(e) => onChange(e.target.value)}
        feedback={false}
        toggleMask
        inputClassName="w-full"
        className={`w-full${invalid ? ' p-invalid' : ''}`}
        placeholder={field.placeholder || ''}
      />
    );
  }
  return <InputText {...common} onChange={(e) => onChange(e.target.value)} />;
};

const ModelCredentialsPanel = ({
  providerType, spec, value, onChange, errors, appId,
}: Props) => {
  const { data: connectionTypes = [] } = useQuery({
    queryKey: ['connection-types', 'dropdown'],
    queryFn: () => connectionTypesService.getAllForDropdown(),
    staleTime: STALE,
  });

  const typeId = useMemo(
    () => connectionTypes.find((t) => t.code === spec?.connectionTypeCode)?._id,
    [connectionTypes, spec?.connectionTypeCode],
  );

  // Two ways to credential a model: create a new connection inline, or REUSE one
  // that already exists in the model's project. Default to "existing" when the
  // model already binds one (edit), else "new".
  const [mode, setMode] = useState<'new' | 'existing'>(value.connectionId ? 'existing' : 'new');

  // Connections in the model's project, of this provider's connection type — the
  // only ones that could actually credential this model.
  const { data: connectionsPage } = useQuery({
    queryKey: ['partner-connections', 'for-model', appId || 'token', typeId || 'any'],
    queryFn: () => partnerConnectionsService.getAll({ page: 1, page_size: 200, include_deleted: false, app_id: appId || undefined } as any),
    enabled: mode === 'existing',
    staleTime: STALE,
  });
  const connectionOptions = useMemo(() => {
    const rows = (connectionsPage as any)?.data || [];
    return rows
      .filter((c: any) => !typeId || c.connectionType === typeId)
      .map((c: any) => ({ label: c.name ? `${c.name}${c.status ? ` · ${c.status}` : ''}` : c._id, value: c._id }));
  }, [connectionsPage, typeId]);

  const chooseExisting = (id: string) => onChange({ ...EMPTY_CREDENTIALS, connectionId: id });
  const switchMode = (next: 'new' | 'existing') => {
    setMode(next);
    // Leaving one mode must not carry its value into the other.
    if (next === 'new') onChange({ ...EMPTY_CREDENTIALS, config: defaultsFor(spec), name: spec ? `${spec.label} — default` : '' });
    else onChange({ ...EMPTY_CREDENTIALS });
  };

  // Clear the per-provider fields when the operator CHANGES provider: an Azure
  // endpoint left over from a previous selection is not a Bedrock region.
  //
  // Guarded on an actual change rather than firing on mount. Opening an existing
  // model sets providerType and the bound connection in the same commit, and an
  // unguarded effect would then run on the next render and wipe the connection
  // the form had just restored.
  const previousProvider = useRef<string | undefined>(providerType);
  useEffect(() => {
    if (previousProvider.current === providerType) return;
    previousProvider.current = providerType;
    onChange({
      ...EMPTY_CREDENTIALS,
      config: defaultsFor(spec),
      name: spec ? `${spec.label} — default` : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerType]);

  const set = (patch: Partial<CredentialValue>) => onChange({ ...value, ...patch });

  if (!providerType) {
    return (
      <p className="text-sm text-ink-muted">
        Select a provider first — its credential fields depend on which one.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {spec && (
        <div className="flex gap-2">
          <button type="button" onClick={() => switchMode('existing')}
            className={`rounded-md border px-3 py-1.5 text-sm ${mode === 'existing' ? 'border-primary bg-primary/10 text-primary' : 'border-line text-ink-muted'}`}>
            Use an existing connection
          </button>
          <button type="button" onClick={() => switchMode('new')}
            className={`rounded-md border px-3 py-1.5 text-sm ${mode === 'new' ? 'border-primary bg-primary/10 text-primary' : 'border-line text-ink-muted'}`}>
            Create new
          </button>
        </div>
      )}

      {spec && mode === 'existing' && (
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Existing connection <span className="text-red-500">*</span>
          </label>
          <Dropdown value={value.connectionId || null} options={connectionOptions} filter
            onChange={(e) => chooseExisting(e.value)}
            placeholder={connectionOptions.length ? 'Select a connection' : `No ${spec.connectionTypeCode} connections in this project`}
            emptyMessage={`No ${spec.connectionTypeCode} connections in this project`} />
          <span className="text-xs text-ink-muted">
            Reuses a connection already created in this project — no new key is stored. The
            key is rotated in Partner Connections.
          </span>
          {errors.name && <span className="text-xs text-red-500">Pick a connection to credential this model.</span>}
        </div>
      )}

      {spec && mode === 'new' && value.connectionId && (
        <p className="text-xs text-ink-muted">
          Credentials are already stored for this model. Leave the fields blank
          to keep them, or enter new ones to replace them.
        </p>
      )}

      {spec && mode === 'new' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Connection Name <span className="text-red-500">*</span>
            </label>
            <InputText
              value={value.name}
              onChange={(e) => set({ name: e.target.value })}
              className={errors.name ? 'p-invalid w-full' : 'w-full'}
              placeholder={`${spec.label} — production`}
            />
            {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
          </div>

          {spec.credentials.map((c) => (
            <div key={c.key} className={`flex flex-col gap-1 ${c.kind === 'json' ? 'md:col-span-2' : ''}`}>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {c.label} {c.required && <span className="text-red-500">*</span>}
              </label>
              <FieldInput
                field={c}
                value={value.secret}
                onChange={(v) => set({ secret: v })}
                invalid={!!errors.secret}
              />
              {errors.secret
                ? <span className="text-xs text-red-500">{errors.secret}</span>
                : c.hint && <span className="text-xs text-ink-muted">{c.hint}</span>}
            </div>
          ))}

          {spec.config.map((f) => (
            <div key={f.key} className={`flex flex-col gap-1 ${f.kind === 'json' ? 'md:col-span-2' : ''}`}>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              <FieldInput
                field={f}
                value={value.config[f.key] ?? ''}
                onChange={(v) => set({ config: { ...value.config, [f.key]: v } })}
                invalid={!!errors[`config.${f.key}`]}
              />
              {errors[`config.${f.key}`]
                ? <span className="text-xs text-red-500">{errors[`config.${f.key}`]}</span>
                : f.hint && <span className="text-xs text-ink-muted">{f.hint}</span>}
            </div>
          ))}

          {!typeId && (
            <div className="md:col-span-2">
              <Tag severity="warning" value={`Missing connection type ${spec.connectionTypeCode}`} />
              <p className="text-xs text-gray-500 mt-1">
                Run <code>python -m migrations.seed_ai_connection_types</code> on the backend
                to add the AI provider connection types.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelCredentialsPanel;
